/**
 * 💳 SPACE CREDITS - Module Gestion des Crédits
 * Suivi des crédits clients style spatial
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, Users, TrendingUp, Award, AlertTriangle,
  Plus, ChevronRight, Loader2, RefreshCw, X, FileText,
  Phone, Calendar, CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Carte stat
const CreditStatCard = ({ icon: Icon, title, value, color = 'red' }) => {
  const colorClasses = {
    red: 'border-red-500/30 bg-red-500/10 text-red-400',
    orange: 'border-orange-500/30 bg-orange-500/10 text-orange-400',
    green: 'border-green-500/30 bg-green-500/10 text-green-400',
    purple: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
  };

  return (
    <div className={`backdrop-blur-xl rounded-2xl p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl border ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-gray-500">{title}</p>
        </div>
      </div>
    </div>
  );
};

// Carte débiteur
const DebtorCard = ({ client, onClick }) => {
  const getBadgeColor = (balance) => {
    if (balance >= 200) return 'bg-red-500 border-red-500';
    if (balance >= 100) return 'bg-yellow-500 border-yellow-500';
    return 'bg-gray-600 border-gray-600';
  };

  const getTextColor = (balance) => {
    if (balance >= 200) return 'text-red-400';
    if (balance >= 100) return 'text-yellow-400';
    return 'text-orange-400';
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      onClick={onClick}
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
        client.currentBalance >= 200 
          ? 'bg-red-500/10 border-red-500/50 hover:border-red-500' 
          : client.currentBalance >= 100 
            ? 'bg-yellow-500/10 border-yellow-500/50 hover:border-yellow-500'
            : 'bg-[#1a1a25] border-gray-700 hover:border-orange-500/50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Indicateur de palier */}
          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-2 ${getBadgeColor(client.currentBalance)}`}>
            {client.currentBalance >= 200 ? '🔴' : client.currentBalance >= 100 ? '🟡' : '🟢'}
          </div>
          <div>
            <div className="font-bold text-white">{client.name}</div>
            {client.phone && (
              <div className="text-sm text-gray-400 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {client.phone}
              </div>
            )}
            {client.lastActivityAt && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Dernier mouvement: {new Date(client.lastActivityAt).toLocaleDateString('fr-FR')}
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-black ${getTextColor(client.currentBalance)}`}>
            {client.currentBalance?.toFixed(2)}€
          </div>
          <div className="flex gap-1 mt-1">
            {client.idPhotoUrl && <span className="text-xs text-green-400">📷 ID</span>}
            {client.signatureUrl && <span className="text-xs text-green-400">✍️ Signé</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Module Crédits principal
const SpaceCredits = () => {
  const [creditsData, setCreditsData] = useState({ clients: [], stats: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // États pour les actions de transaction
  const [actionType, setActionType] = useState(null); // 'add_credit' | 'add_payment'
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Charger les données
  const loadCredits = async () => {
    setIsLoading(true);
    try {
      const data = await api.getCredits?.() || { clients: [], stats: {} };
      setCreditsData(data);
    } catch (error) {
      console.error('Erreur chargement crédits:', error);
      setCreditsData({ clients: [], stats: {} });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCredits();
  }, []);

  // Voir les détails d'un client
  const handleViewClient = async (client) => {
    try {
      const data = await api.getCreditClient?.(client.id);
      // L'API retourne directement le client avec transactions intégrées
      setSelectedClient(data || client);
      setTransactions(data?.transactions || []);
      setShowDetailModal(true);
      // Réinitialiser les états d'action
      setActionType(null);
      setAmount('');
      setNotes('');
    } catch (error) {
      toast.error('Erreur chargement détails');
    }
  };

  // Ajouter un crédit (augmente la dette)
  const handleAddCredit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setIsSubmitting(true);
    try {
      console.log('[DEBUG] Adding credit:', selectedClient.id, amount);
      await api.addCreditTransaction(selectedClient.id, {
        amount: parseFloat(amount),
        type: 'credit',
        notes: notes || 'Crédit ajouté'
      });
      toast.success(`✅ +${amount}€ ajouté au crédit de ${selectedClient.name}`);
      // Recharger les données du client
      const data = await api.getCreditClient(selectedClient.id);
      setSelectedClient(data);
      setTransactions(data?.transactions || []);
      // Recharger la liste globale
      loadCredits();
      // Réinitialiser le formulaire
      setActionType(null);
      setAmount('');
      setNotes('');
    } catch (error) {
      console.error('[DEBUG] Credit error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enregistrer un paiement (réduit la dette)
  const handleAddPayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setIsSubmitting(true);
    try {
      console.log('[DEBUG] Adding payment:', selectedClient.id, amount);
      await api.addCreditTransaction(selectedClient.id, {
        amount: parseFloat(amount),
        type: 'payment',
        notes: notes || 'Remboursement'
      });
      toast.success(`✅ Paiement de ${amount}€ enregistré pour ${selectedClient.name}`);
      // Recharger les données du client
      const data = await api.getCreditClient(selectedClient.id);
      setSelectedClient(data);
      setTransactions(data?.transactions || []);
      // Recharger la liste globale
      loadCredits();
      // Réinitialiser le formulaire
      setActionType(null);
      setAmount('');
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { stats } = creditsData;
  const debtors = (creditsData.clients || []).filter(c => c.currentBalance > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-orange-500 to-red-400">
            💳 CRÉDITS
          </h1>
          <p className="text-gray-500 text-sm">Gestion des crédits clients</p>
        </div>
        <Button
          onClick={loadCredits}
          variant="outline"
          className="border-red-500/30 text-red-400"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <CreditStatCard
          icon={CreditCard}
          title="Total dû"
          value={`${(stats?.totalDebt || 0).toFixed(2)}€`}
          color="red"
        />
        <CreditStatCard
          icon={Users}
          title="Débiteurs actifs"
          value={stats?.activeDebtors || 0}
          color="orange"
        />
        <CreditStatCard
          icon={TrendingUp}
          title="Total remboursé"
          value={`${(stats?.totalPaid || 0).toFixed(2)}€`}
          color="green"
        />
        <CreditStatCard
          icon={Award}
          title="Total accordé"
          value={`${(stats?.totalCreditGiven || 0).toFixed(2)}€`}
          color="purple"
        />
      </div>

      {/* Alertes */}
      {(stats?.alert100 > 0 || stats?.alert200 > 0) && (
        <div className="flex flex-wrap gap-3">
          {stats?.alert100 > 0 && (
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/50 rounded-xl px-4 py-2"
            >
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 font-medium">{stats.alert100} clients entre 100€ et 200€</span>
            </motion.div>
          )}
          {stats?.alert200 > 0 && (
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-2 bg-red-500/20 border border-red-500/50 rounded-xl px-4 py-2"
            >
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-medium">{stats.alert200} clients à 200€ ou plus !</span>
            </motion.div>
          )}
        </div>
      )}

      {/* Liste des débiteurs */}
      <div className="backdrop-blur-xl rounded-2xl p-5 border border-orange-500/30 bg-[#12121a]/60">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-400" />
            📋 Liste des débiteurs
          </h3>
          <Button
            onClick={() => window.open('/team-lpe/search?tab=credits', '_blank')}
            className="bg-gradient-to-r from-orange-500 to-red-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau crédit
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
          </div>
        ) : debtors.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">Aucun crédit en cours 🎉</p>
            <p className="text-gray-600 text-sm mt-2">Tous les clients sont à jour !</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {debtors
              .sort((a, b) => b.currentBalance - a.currentBalance)
              .map((client) => (
                <DebtorCard
                  key={client.id}
                  client={client}
                  onClick={() => handleViewClient(client)}
                />
              ))}
          </div>
        )}
      </div>

      {/* Modal détails client */}
      <Dialog open={showDetailModal} onOpenChange={(open) => {
        setShowDetailModal(open);
        if (!open) {
          setActionType(null);
          setAmount('');
          setNotes('');
        }
      }}>
        <DialogContent className="bg-[#12121a] border border-purple-500/30 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              📜 Crédit: {selectedClient?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-4">
              {/* Solde actuel */}
              <div className={`p-4 rounded-xl text-center ${
                selectedClient.currentBalance > 0 
                  ? 'bg-red-500/10 border border-red-500/30' 
                  : 'bg-green-500/10 border border-green-500/30'
              }`}>
                <p className="text-gray-400 text-sm">Solde actuel</p>
                <p className={`text-3xl font-black ${
                  selectedClient.currentBalance > 0 ? 'text-red-400' : 'text-green-400'
                }`}>
                  {selectedClient.currentBalance?.toFixed(2)}€
                </p>
                {selectedClient.currentBalance >= 100 && (
                  <p className="text-yellow-400 text-sm mt-1 font-medium">
                    ⚠️ Alerte : dette élevée !
                  </p>
                )}
              </div>

              {/* BOUTONS D'ACTION - AJOUT CRÉDIT / PAIEMENT */}
              {!actionType ? (
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => setActionType('add_credit')}
                    className="bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold py-6"
                    data-testid="add-credit-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    ➕ Ajouter crédit
                  </Button>
                  <Button
                    onClick={() => setActionType('add_payment')}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-6"
                    data-testid="add-payment-btn"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    💰 Enregistrer paiement
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 p-4 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="text-white font-semibold">
                    {actionType === 'add_credit' ? '➕ Ajouter au crédit' : '💰 Enregistrer un paiement'}
                  </h4>
                  <div>
                    <label className="text-sm text-gray-400">Montant (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="bg-black/50 border-gray-700 text-white text-lg"
                      autoFocus
                      data-testid="credit-amount-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Notes (optionnel)</label>
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Ex: Achat du 10/01..."
                      className="bg-black/50 border-gray-700 text-white"
                      data-testid="credit-notes-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setActionType(null);
                        setAmount('');
                        setNotes('');
                      }}
                      variant="outline"
                      className="flex-1 border-gray-600"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={actionType === 'add_credit' ? handleAddCredit : handleAddPayment}
                      disabled={isSubmitting || !amount || parseFloat(amount) <= 0}
                      className={`flex-1 ${
                        actionType === 'add_credit' 
                          ? 'bg-red-500 hover:bg-red-600' 
                          : 'bg-green-500 hover:bg-green-600'
                      }`}
                      data-testid="credit-submit-btn"
                    >
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Documents */}
              {(selectedClient.idPhotoUrl || selectedClient.signatureUrl) && (
                <div className="flex gap-3">
                  {selectedClient.idPhotoUrl && (
                    <div className="flex-1 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                      <span className="text-green-400 text-sm">📷 Pièce d&apos;identité enregistrée</span>
                    </div>
                  )}
                  {selectedClient.signatureUrl && (
                    <div className="flex-1 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                      <span className="text-green-400 text-sm">✍️ Signature enregistrée</span>
                    </div>
                  )}
                </div>
              )}

              {/* Transactions */}
              <div>
                <h4 className="text-white font-bold mb-3">📊 Historique des transactions</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {transactions.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Aucune transaction</p>
                  ) : (
                    transactions.map((tx, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-xl border ${
                          tx.type === 'credit' 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : 'bg-green-500/10 border-green-500/30'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-white font-medium">{tx.notes || tx.description || (tx.type === 'credit' ? 'Crédit accordé' : 'Remboursement')}</p>
                            <p className="text-xs text-gray-500">{new Date(tx.createdAt).toLocaleString('fr-FR')}</p>
                          </div>
                          <p className={`font-bold ${tx.type === 'credit' ? 'text-red-400' : 'text-green-400'}`}>
                            {tx.type === 'credit' ? '+' : '-'}{tx.amount?.toFixed(2)}€
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpaceCredits;
